import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import * as TaskActions from 'src/app/store/task/task.actions';

import { environment } from 'src/environments/environment';
import { EventData } from '../model/api/data-rest';
import { WsEventMessage } from '../model/api/ws-event-message';
import { Lang } from '../model/lang';
import { AppState } from '../store/app.reducer';
import { EPermissionStatus, PermissionEvent } from '../model/permission';
import { EEventIds } from '../conf/events';
import * as SiteActions from 'src/app/store/site/site.actions';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Device } from '../model/device';
import { selectCurrentDevice } from '../store/device/device.selector';
import { TasksPollingService } from './task-polling.service';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    socketSub: Subscription;
    socket$: WebSocketSubject<EventData>;
    device$: Observable<Device | undefined>;
    deviceSub: Subscription | undefined;
    currentDevice: Device;

    constructor(
        private store: Store<AppState>,
        private taskPollingService: TasksPollingService,
    ) { }

    muteWebSocket(): void {
        console.log('MUTING WEBSOCKET');
        this.socketSub?.unsubscribe();
        this.deviceSub?.unsubscribe();
    }

    unmuteWebSocket(token: string, lang: Lang): void {
        console.log('UNMUTING WEBSOCKET');
        if (!this.socket$) {
            this.newWebsocket(token, lang);
        }
        this.socketSub = this.socket$.pipe(filter(m => !!m)).subscribe(message => {
            if (!message.Success) {
                console.log('WS: unsuccessful event received', JSON.stringify(message));
                return;
            }
            this.dispatchMessage(message.Data);
        });
    }

    newWebsocket(token: string, lang: Lang, userId?: number): void {
        let url = environment.websocketUrl + '?language=' + lang.code;
        if (userId) {
            url += '&userId=' + userId;
        }
        this.socket$ = webSocket({
            url,
            protocol: [token],
        });
        this.device$ = this.store.select(selectCurrentDevice);
        this.deviceSub = this.device$.subscribe(currentDevice => {
            this.currentDevice = currentDevice;
        });
    }

    dispatchMessage(wsMessage: WsEventMessage): void {
        let permissionEvent: PermissionEvent;
        switch (wsMessage.EventId) {
            case EEventIds.PermissionCreated:
                //TODOREPLACE this.store.dispatch(PermissionActions.permissionCreatedNotification({ message: wsMessage.Message }));
                /*let not = new Notification("NOTIFICATIONS", wsMessage.Message, NotificationLevel.Success, '');TODO CHECK
                this.store.dispatch(NotificationsActions.addNotification({ notification: not, toastOnly: true }));*/
                break;
            case EEventIds.PermissionReady:
                permissionEvent = JSON.parse(wsMessage.Data);
                this.store.dispatch(PermissionActions.permissionStatusNotification({
                    permissionId: permissionEvent.PermissionId,
                    status: EPermissionStatus.Ready,
                    message: wsMessage.Message,
                }));
                break;
            case EEventIds.PermissionActive:
                permissionEvent = JSON.parse(wsMessage.Data);
                this.store.dispatch(PermissionActions.permissionStatusNotification({
                    permissionId: permissionEvent.PermissionId,
                    status: EPermissionStatus.Active,
                    message: wsMessage.Message,
                }));
                break;
            case EEventIds.PermissionExpired:
                permissionEvent = JSON.parse(wsMessage.Data);
                this.store.dispatch(PermissionActions.permissionStatusNotification({
                    permissionId: permissionEvent.PermissionId,
                    status: EPermissionStatus.Expired,
                    message: wsMessage.Message,
                }));
                break;
            case EEventIds.ConnectedAutomationCreated:
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId: wsMessage.DeviceId }));
                }
                break;
            case EEventIds.ConnectedAutomationRemoved:
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId: wsMessage.DeviceId }));
                }
                break;
            case EEventIds.DeviceConfigured:
                this.store.dispatch(SiteActions.getSite({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId: wsMessage.DeviceId }));
                }
                this.dispatchUpdate(wsMessage);
                break;
            case EEventIds.GatewayConfigured:
                this.store.dispatch(SiteActions.getSite({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId: wsMessage.DeviceId }));
                }
                this.dispatchUpdate(wsMessage);
                break;
            case EEventIds.GatewayConfigured2:
                this.store.dispatch(SiteActions.getSite({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId: wsMessage.DeviceId }));
                }
                this.dispatchUpdate(wsMessage);
                break;
            case EEventIds.DeviceOnline:
                this.store.dispatch(SiteActions.getSitesOnlineDevices({}));
                this.dispatchUpdate(wsMessage);
                break;
            case EEventIds.DeviceRemoved:
                this.store.dispatch(SiteActions.getSitesOnlineDevices({}));
                this.dispatchUpdate(wsMessage);
                break;
            case EEventIds.TaskCreate:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.taskPollingService.startPolling(this.currentDevice);
                }
                break;
            case EEventIds.TaskStarted:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.taskPollingService.startPolling(this.currentDevice);
                }
                break;
            case EEventIds.TaskPending:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.taskPollingService.startPolling(this.currentDevice);
                }
                break;
            case EEventIds.TaskRunning:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.taskPollingService.startPolling(this.currentDevice);
                }
                break;
            case EEventIds.TaskAborting:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    this.taskPollingService.startPolling(this.currentDevice);
                }
                break;
            case EEventIds.TaskAborted:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    console.log('--TaskAborted--');
                    console.log(this.currentDevice);
                    console.log(wsMessage.DeviceId);
                    this.taskPollingService.stopPollingWsc();
                    console.log('**TaskAborted**');
                }
                break;
            case EEventIds.TaskError:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    console.log('--TaskError--');
                    console.log(this.currentDevice);
                    console.log(wsMessage.DeviceId);
                    console.log('**TaskError**');
                    this.taskPollingService.stopPollingWsc();
                }
                break;
            case EEventIds.TaskDone:
                this.store.dispatch(TaskActions.getActiveTasks({}));
                if (this.currentDevice !== undefined && wsMessage.DeviceId === this.currentDevice.Id) {
                    console.log('--TaskDone--');
                    console.log(this.currentDevice);
                    console.log(wsMessage.DeviceId);
                    this.taskPollingService.stopPollingWsc();
                    console.log('**TaskDone**');
                }
                break;
            default:
                this.dispatchUpdate(wsMessage);
        }
    }

    dispatchUpdate(wsMessage: WsEventMessage) {
        const event: WsEventMessage = { ...wsMessage, DeviceState: JSON.parse(wsMessage.Data) };
        this.store.dispatch(DeviceActions.deviceStateUpdate({ deviceId: event.DeviceId, event }));
    }
}
